/**
 * Tetrish - Score Manager Service
 * Handles scoring, level progression, and high score tracking
 */

import {
	LINES_PER_LEVEL,
	BACK_TO_BACK_MULTIPLIER,
	ALL_CLEAR_BONUS_BASE,
	LINE_POINTS,
	TSPIN_POINTS,
	TSPIN_MINI_POINTS,
} from '../constants.js';

export class ScoreManager {
	constructor() {
		this.score = 0;
		this.lines = 0;
		this.level = 1;
		this.backToBack = false;
		this.highScore = this.loadHighScore();

		this.linePoints = LINE_POINTS;
		this.tspinPoints = TSPIN_POINTS;
		this.tspinMiniPoints = TSPIN_MINI_POINTS;
		this.levelThreshold = LINES_PER_LEVEL;
	}

	/**
	 * Reset scores for a new game
	 */
	resetScore() {
		this.score = 0;
		this.lines = 0;
		this.level = 1;
		this.backToBack = false;
	}

	/**
	 * Add general points to the score
	 */
	addPoints(points) {
		this.score += points;
	}

	/**
	 * Add points for dropping a piece
	 */
	addDropScore(dropDistance) {
		// 2 points per cell dropped
		const points = dropDistance * 2;
		this.score += points;
	}

	/**
	 * Add score for clearing lines.
	 * Handles standard clears, T-spins, back-to-back bonus, and all-clear bonus.
	 * @param {number} linesCleared
	 * @param {{ isTspin?: boolean, isTspinMini?: boolean, isAllClear?: boolean }} options
	 */
	addLinesClearedScore(linesCleared, { isTspin = false, isTspinMini = false, isAllClear = false } = {}) {
		let basePoints;

		if (isTspin) {
			basePoints = this.tspinPoints[linesCleared] ?? 0;
		} else if (isTspinMini) {
			basePoints = this.tspinMiniPoints[linesCleared] ?? 0;
		} else {
			basePoints = this.linePoints[linesCleared] ?? 0;
		}

		// A clear is "special" (eligible for B2B chain) if it's a Tetris or T-spin with lines
		const isSpecial = linesCleared === 4 || (isTspin && linesCleared > 0);

		// Back-to-back bonus when two consecutive specials are back-to-back
		if (isSpecial && this.backToBack) {
			basePoints = Math.round(basePoints * BACK_TO_BACK_MULTIPLIER);
		}

		// Update back-to-back chain (only changes when lines are actually cleared)
		if (linesCleared > 0) {
			this.backToBack = isSpecial;
		}

		const points = basePoints * this.level;
		this.score += points;
		this.lines += linesCleared;

		// All-clear bonus: board fully emptied
		if (isAllClear) {
			this.score += ALL_CLEAR_BONUS_BASE * this.level;
		}
	}

	/**
	 * Check if the player should level up
	 * Returns true if level increased
	 */
	checkLevelUp() {
		const newLevel = Math.floor(this.lines / this.levelThreshold) + 1;

		if (newLevel > this.level) {
			this.level = newLevel;
			return true;
		}

		return false;
	}

	/**
	 * Update the high score if the current score is higher
	 */
	updateHighScore() {
		if (this.score > this.highScore) {
			this.highScore = this.score;
			this.saveHighScore();
			return true;
		}
		return false;
	}

	/**
	 * Save the high score to localStorage
	 */
	saveHighScore() {
		try {
			localStorage.setItem('tetrish-high-score', this.highScore.toString());
			return true;
		} catch (e) {
			console.error('Error saving high score:', e);
			return false;
		}
	}

	/**
	 * Load high score from localStorage
	 */
	loadHighScore() {
		try {
			const storedScore = localStorage.getItem('tetrish-high-score');
			return storedScore ? parseInt(storedScore, 10) : 0;
		} catch (e) {
			console.error('Error loading high score:', e);
			return 0;
		}
	}
}
